import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import SEO from "@/components/SEO";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, error, already_verified
  const [message, setMessage] = useState("");
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");
      
      if (!token) {
        setStatus("error");
        setMessage("No verification token provided");
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-email/${token}`);
        
        if (response.data.already_verified) {
          setStatus("already_verified");
          setMessage(response.data.message);
        } else {
          setStatus("success");
          setMessage(response.data.message);
          setUserInfo({
            email: response.data.email,
            username: response.data.username
          });
        }
      } catch (error) {
        setStatus("error");
        setMessage(error.response?.data?.detail || "Failed to verify email. The link may have expired.");
        console.error("Email verification error:", error);
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleContinue = () => {
    navigate("/account");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900">
      <SEO 
        title="Email Verification | George Dives"
        description="Verify your email address to complete your registration"
      />
      
      <Navigation />

      <div className="container mx-auto px-4 py-12 flex items-center justify-center min-h-[calc(100vh-100px)]">
        <Card className="bg-white/5 backdrop-blur-md border-white/10 text-white max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {status === "loading" && (
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-white animate-spin" />
                </div>
              )}
              {status === "success" && (
                <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-bounce">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
              )}
              {status === "already_verified" && (
                <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
              )}
              {status === "error" && (
                <div className="w-20 h-20 bg-gradient-to-br from-red-400 to-orange-500 rounded-full flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-white" />
                </div>
              )}
            </div>
            
            {status === "loading" && (
              <>
                <CardTitle className="text-2xl mb-3">Verifying Your Email...</CardTitle>
                <CardDescription className="text-gray-300">
                  Please wait while we verify your email address
                </CardDescription>
              </>
            )}
            
            {status === "success" && (
              <>
                <CardTitle className="text-2xl mb-3 text-green-400">
                  Email Verified Successfully!
                </CardTitle>
                <CardDescription className="text-gray-300">
                  Your email has been verified. You can now access all features.
                </CardDescription>
              </>
            )}
            
            {status === "already_verified" && (
              <>
                <CardTitle className="text-2xl mb-3 text-cyan-400">
                  Email Already Verified
                </CardTitle>
                <CardDescription className="text-gray-300">
                  Your email was already verified. You&apos;re all set!
                </CardDescription>
              </>
            )}
            
            {status === "error" && (
              <>
                <CardTitle className="text-2xl mb-3 text-red-400">
                  Verification Failed
                </CardTitle>
                <CardDescription className="text-gray-300">
                  {message}
                </CardDescription>
              </>
            )}
          </CardHeader>
          
          <CardContent>
            {status === "success" && userInfo && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-300 mb-2">
                  <strong>Username:</strong> @{userInfo.username}
                </p>
                <p className="text-sm text-gray-300">
                  <strong>Email:</strong> {userInfo.email}
                </p>
              </div>
            )}
            
            {(status === "success" || status === "already_verified") && (
              <Button
                onClick={handleContinue}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white text-lg py-6"
              >
                Continue to Login
              </Button>
            )}
            
            {status === "error" && (
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/register")}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  Create New Account
                </Button>
                <Button
                  onClick={() => navigate("/account")}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Back to Login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <FloatingUserMenu />
      <Footer />

    </div>
  );
};

export default VerifyEmail;
